import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GameState } from "@yamber/rules-engine";
import { apiClient, type GameStats } from "../api/client";

interface LocalGameRecord {
  id: string;
  templateId: string;
  status: "in_progress" | "completed" | "abandoned";
  finalScore: number | null;
  startedAt: number;
  completedAt: number | null;
  turns: Array<{
    turnNumber: number;
    rolls: Array<{
      dice: number[];
      holds: boolean[];
      timestamp: number;
    }>;
    scoredCell: {
      columnId: string;
      category: string;
      score: number;
    } | null;
  }>;
  sheetSnapshot: Record<string, unknown>;
  syncStatus: "local" | "synced" | "pending" | "conflict";
  version: number;
  updatedAt: number;
}

interface HistoryState {
  games: LocalGameRecord[];
  stats: GameStats | null;
  lastSyncTime: number;
  isSyncing: boolean;

  // Actions
  saveLocalGame: (game: GameState, status: "in_progress" | "completed" | "abandoned") => void;
  loadLocalGame: (id: string) => LocalGameRecord | undefined;
  deleteLocalGame: (id: string) => void;
  getCompletedGames: () => LocalGameRecord[];
  syncWithCloud: () => Promise<void>;
  loadStats: () => Promise<void>;
  calculateLocalStats: () => GameStats;
}

export const useHistoryStore = create<HistoryState>()(
  persist(
    (set, get) => ({
      games: [],
      stats: null,
      lastSyncTime: 0,
      isSyncing: false,

      saveLocalGame: (game: GameState, status) => {
        const existingIndex = get().games.findIndex((g) => g.id === game.id);
        const now = Date.now();

        const player = game.players[0];
        const record: LocalGameRecord = {
          id: game.id,
          templateId: game.templateId,
          status,
          finalScore: status === "completed" ? (player?.totalScore ?? 0) : null,
          startedAt: game.createdAt,
          completedAt: status === "completed" ? now : null,
          turns: [], // Simplified - would track full turn history
          sheetSnapshot: player?.sheet ? JSON.parse(JSON.stringify(player.sheet)) : {},
          syncStatus: "local",
          version: existingIndex >= 0 ? (get().games[existingIndex]?.version ?? 0) + 1 : 1,
          updatedAt: now,
        };

        set((state) => {
          const games = [...state.games];
          if (existingIndex >= 0) {
            games[existingIndex] = record;
          } else {
            games.unshift(record);
          }
          return { games };
        });
      },

      loadLocalGame: (id: string) => {
        return get().games.find((g) => g.id === id);
      },

      deleteLocalGame: (id: string) => {
        set((state) => ({
          games: state.games.filter((g) => g.id !== id),
        }));
      },

      getCompletedGames: () => {
        return get().games.filter((g) => g.status === "completed");
      },

      syncWithCloud: async () => {
        const token = apiClient.getToken();
        if (!token) return;

        set({ isSyncing: true });
        try {
          const localGames = get().games.filter(
            (g) => g.syncStatus === "local" || g.syncStatus === "pending"
          );

          const result = await apiClient.syncGames(
            localGames.map((g) => ({
              id: g.id,
              templateId: g.templateId,
              status: g.status,
              finalScore: g.finalScore,
              startedAt: g.startedAt,
              completedAt: g.completedAt,
              turns: g.turns,
              sheetSnapshot: g.sheetSnapshot,
              version: g.version,
            })),
            get().lastSyncTime
          );

          // Update local games with server responses
          set((state) => {
            const updatedGames = [...state.games];

            for (const serverGame of result.games) {
              const localIndex = updatedGames.findIndex((g) => g.id === serverGame.id);
              const isConflict = result.conflicts.includes(serverGame.id);

              const updatedRecord: LocalGameRecord = {
                id: serverGame.id,
                templateId: serverGame.templateId,
                status: serverGame.status,
                finalScore: serverGame.finalScore,
                startedAt: serverGame.startedAt,
                completedAt: serverGame.completedAt,
                turns: serverGame.turns as LocalGameRecord["turns"],
                sheetSnapshot: serverGame.sheetSnapshot as Record<string, unknown>,
                syncStatus: isConflict ? "conflict" : "synced",
                version: serverGame.version,
                updatedAt: serverGame.updatedAt,
              };

              if (localIndex >= 0) {
                updatedGames[localIndex] = updatedRecord;
              } else {
                updatedGames.push(updatedRecord);
              }
            }

            return {
              games: updatedGames.sort((a, b) => b.updatedAt - a.updatedAt),
              lastSyncTime: Date.now(),
            };
          });
        } catch (error) {
          console.error("Sync failed:", error);
        } finally {
          set({ isSyncing: false });
        }
      },

      loadStats: async () => {
        const token = apiClient.getToken();
        if (!token) {
          // Use local stats
          set({ stats: get().calculateLocalStats() });
          return;
        }

        try {
          const stats = await apiClient.getStats();
          set({ stats });
        } catch {
          // Fallback to local stats
          set({ stats: get().calculateLocalStats() });
        }
      },

      calculateLocalStats: () => {
        const games = get().games;
        const completedGames = games.filter((g) => g.status === "completed");

        const stats: GameStats = {
          totalGames: games.length,
          completedGames: completedGames.length,
          averageScore: 0,
          highScore: 0,
          lowScore: 0,
          categoryStats: {},
          templateStats: {},
        };

        if (completedGames.length === 0) {
          return stats;
        }

        const scores = completedGames
          .map((g) => g.finalScore)
          .filter((s): s is number => s !== null);

        if (scores.length > 0) {
          stats.averageScore = Math.round(
            scores.reduce((a, b) => a + b, 0) / scores.length
          );
          stats.highScore = Math.max(...scores);
          stats.lowScore = Math.min(...scores);
        }

        // Calculate template stats
        for (const game of completedGames) {
          const templateId = game.templateId;
          if (!stats.templateStats[templateId]) {
            stats.templateStats[templateId] = {
              gamesPlayed: 0,
              averageScore: 0,
              highScore: 0,
            };
          }

          const templateStat = stats.templateStats[templateId];
          if (templateStat) {
            templateStat.gamesPlayed++;
            if (game.finalScore !== null) {
              templateStat.highScore = Math.max(templateStat.highScore, game.finalScore);
            }
          }
        }

        // Calculate template averages
        for (const [templateId, templateStat] of Object.entries(stats.templateStats)) {
          const templateGames = completedGames.filter((g) => g.templateId === templateId);
          const templateScores = templateGames
            .map((g) => g.finalScore)
            .filter((s): s is number => s !== null);

          if (templateScores.length > 0) {
            templateStat.averageScore = Math.round(
              templateScores.reduce((a, b) => a + b, 0) / templateScores.length
            );
          }
        }

        return stats;
      },
    }),
    {
      name: "yambpro-history",
    }
  )
);
